<template>
  <div>
    <div class="cta-wrapper">
      <p class="headline">{{ $t('HOUSE_BOARD_NOTIFICATION_TITLE') }}</p>
      <sh-button
        v-if="hasMoreNotifications"
        naked="true"
        @click="handleSeeMoreNotifications"
      >{{ $t('HOUSE_BOARD_NOTIFICATION_SEE_ALL_CTA') }}</sh-button>
    </div>

    <notification-list
      :isSlideable="$device.isMobileOrTablet"
      :notifications="lastThreeNotifications"
      class="notifications-list"
    />

    <div v-if="!isFriend" class="local-house-link">
      <!-- TODO: Use nuxt-link once venues serves the right slug for local house -->
      <nuxt-link
        class="headline"
        :to="localHouse.housePath"
      >{{ localHouse.name }} &gt;</nuxt-link>
    </div>

    <div class="details spaced">
      <div v-if="!isFriend">
        <p>{{ $t('HOUSE_BOARD_HOUSE_OPEN_STATUS') }}</p>
        <p class="description">{{ openingHour }} - {{ closingHour }}</p>
      </div>
      <sh-button
        outline="true"
        color="secondary"
        :to="localePath({ name: 'settings-membership' })"
        :fullWidth="isFriend"
      >{{ $t('HOUSE_BOARD_MEMBERSHIP_CARD_LABEL') }}</sh-button>
    </div>

    <div class="spaced">
      <sh-button
        class="room-cta"
        color="secondary"
        size="medium"
        fullWidth="true"
        :to="localePath({ path: '/bedrooms' })"
      >{{ $t('HOUSE_BOARD_BOOK_ROOM_CTA') }}</sh-button>
    </div>

    <div class="house-details">
      <nuxt-link
        class="house-links"
        :to="localePath({ name: 'contact' })"
      >{{ contactLabel }}</nuxt-link>
      <!-- TODO: Use nuxt-link once venues serves the right slug for local house -->
      <nuxt-link
        class="house-links"
        :to="rulesPath"
      >{{ rulesLabel }}</nuxt-link>
    </div>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import { ShBlade } from '@sohohouse/design-system'
  import { decorators, CONSTANTS } from '@sohohouse/platform-resources'

  import NotificationList from '~/components/notification-list'
  import UIMixin from '~/mixins/ui'
  import { VIEWS } from '~/constants/house-board-views'
  import { ROUTE_SITECORE_JSS } from '~/constants/routes'

  const { mapGetters: mapAccountGetters } = createNamespacedHelpers('user/account')
  const { mapActions: mapModalsActions } = createNamespacedHelpers('ui/modals')
  const {
    mapGetters: mapNotificationGetters,
    mapActions: mapNotificationActions
  } = createNamespacedHelpers('notifications')
  const { mapGetters: mapMembershipGetters } = createNamespacedHelpers('user/membership')

  const { DateTime } = decorators.shared

  export default {
    components: {
      ShBlade,
      NotificationList
    },

    mixins: [
      UIMixin
    ],

    computed: {
      ...mapAccountGetters(['localHouse']),
      ...mapNotificationGetters(['unreadNotifications', 'showNotificationAlert']),
      ...mapMembershipGetters(['membership']),

      openingHour () {
        const { opensAt } = this.localHouse.operatingHours || {}
        return this.dateToHour(opensAt)
      },

      closingHour () {
        const { closesAt } = this.localHouse.operatingHours || {}
        return this.dateToHour(closesAt)
      },

      lastThreeNotifications () {
        return this.unreadNotifications.slice(0, 3)
      },

      hasMoreNotifications () {
        return this.unreadNotifications.length > 3
      },

      isFriend () {
        return this.membership.subscriptionType === CONSTANTS.SUBSCRIPTION_TYPES.FRIENDS
      },

      contactLabel () {
        return this.isFriend ? this.$t('HOUSE_BOARD_CONTACT') : this.$t('HOUSE_BOARD_CONTACT_HOUSE')
      },

      rulesLabel () {
        return this.isFriend ? this.$t('HOUSE_BOARD_FRIENDS_RULES') : this.$t('HOUSE_BOARD_HOUSE_RULES')
      },

      rulesPath () {
        return this.isFriend ? this.localePath({ name: ROUTE_SITECORE_JSS, params: { pathMatch: 'terms-and-policies/soho-friends-rules' } }) : this.localHouse.houseRulesPath
      }
    },

    mounted () {
      this.setUnseenNotifications()
    },

    watch: {
      showNotificationAlert: 'setUnseenNotifications'
    },

    methods: {
      ...mapModalsActions(['close']),
      ...mapNotificationActions(['markNotificationsAsSeen']),

      dateToHour (t) {
        const d = new DateTime(t)

        return this.$d(d.withOffset.epoch, 'time')
      },

      closeHouseBoard () {
        this.$ui.modal.close(this.name)
      },

      handleSeeMoreNotifications () {
        this.$emit('handleViewChange', VIEWS.NOTIFICATION)
      },

      setUnseenNotifications () {
        if (
          this.showNotificationAlert
          && this.unreadNotifications
          && this.unreadNotifications.length > 0
        ) {
          const [notification] = this.unreadNotifications
          this.markNotificationsAsSeen(notification.notificationGroup)
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
.board-wrapper {
  padding: $space-050;

  @include tablet {
    padding: $space-050 $space-060;
  }

  .local-house-link {
    margin-bottom: $space-070;
  }

  .notifications-list {
    margin-bottom: $space-070;
  }

  .spaced {
    margin-bottom: $space-070;
  }

  .welcome {
    margin-top: $space-010;
    margin-bottom: $space-060;
  }

  .details {
    display: flex;
    justify-content: space-between;

    @include mobile-sm {
      display: block;
      margin-bottom: $space-050;

      div {
        margin-bottom: $space-050;
      }

      a {
        width: 100%;
      }
    }
  }

  .house-details {
    a {
      display: block;
    }

    :first-child {
      margin-bottom: $space-070;
    }
  }
}

p,
a:not(.room-cta) {
  @include font("040");
  color: $color-white;

  &.description {
    color: $color-gray_5;
  }

  &.headline {
    @include font("060");

    color: $color-white;
  }

  &.house-links {
    @include fontBold("040");
  }
}

.cta-wrapper {
  @include font("040");

  display: flex;
  justify-content: space-between;
  margin-bottom: $space-030;

  button {
    color: white;
  }
}
</style>
